/**
 * Разбор комментариев под нашими публикациями: вердикты модели и удаление.
 *
 * Ходит в Node-бэкенд напрямую по /api, а не через edge-функции: там живут
 * токены Graph API и ключ модели.
 *
 * Все три ручки требуют входа на сайт: разбор тратит деньги на модель, а
 * удаление необратимо — открытыми их оставлять нельзя.
 */
import { getAuthHeaders } from '../../features/statistics/api/auth-headers';
export type ModerationAction = 'delete' | 'review' | 'keep';

export interface ModerationVerdict {
  action: ModerationAction;
  category: string;
  reason: string;
}

export interface ModerationComment {
  id: string;
  mediaId: string;
  postUrl: string;
  parentId: string | null;
  username: string;
  text: string;
  likeCount: number;
  timestamp: string | null;
  hidden: boolean;
  verdict: ModerationVerdict;
  /** Ответ уйдёт вместе с родителем — отдельно удалять не нужно. */
  goesWithParent: boolean;
}

export interface ClassifyResult {
  posts: Array<{
    url: string;
    username: string;
    total: number;
    error?: string;
    /** Комментариев на посту больше, чем отдал API: разобрано не всё. */
    truncated?: boolean;
  }>;
  comments: ModerationComment[];
  counts: Record<string, number>;
}

/** Что отправляем на удаление. Текст и вердикт — для журнала на сервере. */
export interface DeleteItemInput {
  id: string;
  /** Ник паблика, под чьим постом комментарий: по нему сервер берёт токен. */
  username: string;
  author?: string;
  postUrl?: string;
  text?: string;
  category?: string;
  reason?: string;
}

export interface DeleteResult {
  deleted: string[];
  alreadyGone: string[];
  failed: Array<{ id: string; error: string }>;
  /** Остаток пачки, который сервер не тронул: прогон остановлен. */
  remaining: string[];
  /** Причина остановки словами — антиспам Instagram или суточный потолок. */
  stopped?: string;
  spentLastDay: number;
  dailyLimit: number;
}

/** Запись журнала удалений: что удалили, почему и кто нажал. */
export interface DeletionRecord {
  commentId: string;
  account: string;
  author: string;
  postUrl?: string;
  text?: string;
  category?: string;
  reason?: string;
  result: 'deleted' | 'already_gone';
  by?: string;
  deletedAt: string;
}

export interface PolicyResult {
  thresholds: Record<string, number>;
  categories: Record<string, string>;
  /** Потолок удалений в сутки, израсходованное (null — журнал недоступен) и размер пачки. */
  daily: { limit: number; spent: number | null; batch: number };
}

async function post<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, {
    method: 'POST',
    headers: await getAuthHeaders(),
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((data as { error?: string }).error ?? `Ошибка ${res.status}`);
  return data as T;
}

export const commentModerationApi = {
  /** Ссылок за раз не больше пяти: разбор идёт синхронно. */
  classify: (links: string[]) =>
    post<ClassifyResult>('/api/moderation/comments/classify', { links }),

  /** Не больше пачки из policy.daily.batch: дальше сервер отвечает 400. */
  remove: (comments: DeleteItemInput[]) =>
    post<DeleteResult>('/api/moderation/comments/delete', { comments }),

  async policy(): Promise<PolicyResult> {
    const res = await fetch('/api/moderation/comments/policy', { headers: await getAuthHeaders() });
    if (!res.ok) throw new Error(`Ошибка ${res.status}`);
    return (await res.json()) as PolicyResult;
  },

  /** История удалений, новые сверху. */
  async journal(): Promise<{ entries: DeletionRecord[] }> {
    const res = await fetch('/api/moderation/comments/journal', { headers: await getAuthHeaders() });
    if (!res.ok) throw new Error(`Ошибка ${res.status}`);
    return (await res.json()) as { entries: DeletionRecord[] };
  },
};
